import { useMemo, useState } from 'react';
import type { ExerciseRow } from '@st/contract';
import { useTranslation } from '../../../i18n/I18nProvider';
import { Chip, EmptyState, Input } from '../../../ui';
import { CATEGORY_KEY } from './categories';

/**
 * Tražilica biblioteke: naziv i kategorija. Filtrira se lokalno jer cijela
 * biblioteka ionako dolazi jednim zahtjevom (`GET /v1/trainer/exercises`).
 */
export function ExercisePicker({
  library,
  onPick,
}: {
  library: readonly ExerciseRow[];
  onPick: (exercise: ExerciseRow) => void;
}) {
  const { t, locale } = useTranslation();
  const [term, setTerm] = useState('');
  const [category, setCategory] = useState<string | null>(null);

  const categories = useMemo(
    () => [...new Set(library.map((e) => e.category))].sort(),
    [library],
  );

  const results = useMemo(() => {
    const needle = term.trim().toLowerCase();
    return library.filter((e) => {
      if (category && e.category !== category) return false;
      if (!needle) return true;
      return (
        e.nameHr.toLowerCase().includes(needle) ||
        e.nameEn.toLowerCase().includes(needle) ||
        (e.equipment ?? '').toLowerCase().includes(needle)
      );
    });
  }, [library, term, category]);

  return (
    <div className="flex flex-col gap-3">
      <Input
        id="exercise-search"
        type="search"
        label={t('builder.searchExercise')}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
      />

      <div className="flex flex-wrap gap-2">
        <Chip selected={category === null} onClick={() => setCategory(null)}>
          {t('builder.allCategories')}
        </Chip>
        {categories.map((c) => (
          <Chip key={c} selected={category === c} onClick={() => setCategory(c)}>
            {t(CATEGORY_KEY[c] ?? c)}
          </Chip>
        ))}
      </div>

      {results.length === 0 ? (
        <EmptyState message={t('builder.noSearchResults')} />
      ) : (
        <ul className="max-h-72 overflow-y-auto rounded-xl border border-line-subtle">
          {results.map((e) => (
            <li key={e.id} className="border-b border-line-subtle last:border-b-0">
              <button
                type="button"
                onClick={() => onPick(e)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors duration-200 hover:bg-bg-alt"
              >
                <span className="min-w-0">
                  <span className="block truncate text-[14.5px] text-ink">
                    {locale === 'en' ? e.nameEn : e.nameHr}
                  </span>
                  <span className="block text-[12px] text-ink-muted">
                    {t(CATEGORY_KEY[e.category] ?? e.category)}
                    {e.equipment ? ` · ${e.equipment}` : ''}
                  </span>
                </span>
                <span aria-hidden="true" className="text-[18px] text-accent">
                  +
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
